import { IPaginationQuery } from "../Typings/MessageText";
import { createURI } from "../Utilities/createURI";

type BaseFetcher = (path: string, options?: RequestInit) => Promise<any>;
type Fetcher = (path: string, options?: RequestInit, rootUrl?: string, isBlob?: boolean) => Promise<any>;
/**
 * A helper function to simplify the use of the fetch API.
 * @param path The API path to call
 * @param options Options to pass to the fetch call
 * @param rootUrl The root URL to use for the fetch call
 * @returns Response as blob, JSON or text; otherwise throws an error if the response is not ok
 */
export const fetcher: Fetcher = async function (path, options, rootUrl, isBlob = false) {
  if (!rootUrl) {
    if (window.parent.Cypress !== undefined) {
      rootUrl = import.meta.env.VITE_BACKEND_API ?? "https://localhost:44392";
    } else {
      rootUrl = import.meta.env.VITE_BACKEND_API;
    }
  }
  const response = await fetch(`${rootUrl}/api${path}`, options);
  if (!response.ok) {
    // Preserve the HTTP status on the thrown error so callers can distinguish an auth
    // rejection (401/403) from a transient failure (5xx/network). Additive: the value is
    // still an Error, so existing catch sites are unaffected.
    const error: Error & { status?: number } = new Error(response.statusText);
    error.status = response.status;
    throw error;
  }

  if (isBlob) {
    return await response.blob();
  }

  // If the content type of the response is JSON, parse it as JSON
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return await response.json();
  }

  // Otherwise, return the response as plain text
  return await response.text();
};

/**
 * A helper function to simplify the use of the fetch API.
 * @param path The API path to call
 * @param options Options to pass to the fetch call
 * @param rootUrl The root URL to use for the fetch call
 * @returns Raw response object
 */
export const rawFetcher: Fetcher = async function (path, options, rootUrl, isBlob = false) {
  if (!rootUrl) {
    if (window.parent.Cypress !== undefined) {
      rootUrl = import.meta.env.VITE_BACKEND_API ?? "https://localhost:44392";
    } else {
      rootUrl = import.meta.env.VITE_BACKEND_API;
    }
  }
  return await fetch(`${rootUrl}/api${path}`, options);
};

export const secureFetcher = async function (
  path: string,
  token: string,
  options?: RequestInit | undefined,
  rootUrl?: string | undefined,
  isBlob = false,
) {
  const secureOptions = {
    ...options,
  };

  secureOptions.headers = {
    ...options?.headers,
    Authorization: `Bearer ${token}`,
  };

  return await fetcher(path, secureOptions, rootUrl, isBlob);
};

export const fetcherNASAdmin: BaseFetcher = async (path, options) =>
  await fetcher(path, options, import.meta.env.VITE_NASADMIN_API);

export const abacApiFetcher: BaseFetcher = async (path, options) =>
  await fetcher(path, options, import.meta.env.VITE_ABAC_API);

export const createPaginationQueryString = (query: IPaginationQuery) => {
  const params = [] as string[][];
  if (query.page !== undefined) {
    params.push(["page", (query.page > 0 ? query.page : 1).toString()]);
  }
  if (query.pageSize !== undefined) {
    params.push(["pageSize", (query.pageSize > 0 ? query.pageSize : 10).toString()]);
  }
  if (query.search !== undefined && query.search.length) {
    params.push(["search", createURI(query.search)]);
  }
  if (query.sortBy !== undefined && query.sortBy.length) {
    params.push(["sortBy", query.sortBy]);
  }
  if (query.sortDirection !== undefined && query.sortDirection.length) {
    params.push(["sortDirection", query.sortDirection]);
  }
  return params.length > 0 ? `?${params.map((p) => p.join("=")).join("&")}` : "";
};

// API Paths
// CandidateProfile
// GET
// /api/candidateprofile/{candidateId}
// POST
// /api/candidateprofile/note/save
// POST
// /api/candidateprofile/review/save
// POST
// /api/candidateprofile/review/vote
export const apiPathCandidateProfile = "/candidateprofile/";

// Candidates
// GET
// /api/candidates/{clientId}
// POST
// /api/candidates/import/{clientId}
// PUT
// /api/candidates/{clientId}/{candidateId}/status
export const apiPathCandidates = "/candidates/";

// CandidateStatusDefinitions
// GET
// /api/candidatestatusdefinitions/{clientId}
// PUT
// /api/candidatestatusdefinitions/{clientId}/{color}
export const apiPathCandidateStatusDefinitions = "/candidatestatusdefinitions/";

// Clients
// GET
// /api/clients
export const apiPathClients = "/clients/";

// Group
// DELETE
// /api/group/client/{clientId}/group/{groupId}
// DELETE
// /api/group/client/{clientId}/group/{groupId}/candidate/{candidateId}
// GET
// /api/group/client/{clientId}
// GET
// /api/group/{clientId}/{groupId}
// POST
// /api/group/save
export const apiPathGroup = "/group/";

export const apiPathUserProfile = "/userprofile/";

// SearchFilter
// DELETE
// /api/searchfilter/{clientId}/{filterId}
// GET
// /api/searchfilter/tags
// GET
// /api/searchfilter/titles
// GET
// /api/searchfilter/{clientId}/{userId}
// POST
// /api/searchfilter/save
export const apiPathSearchFilter = "/searchfilter/";
// SearchFilter
// GET
// /api/message/history/candidate/{candidateId}
export const apiMessaging = "/message/";

// Campaigns
// GET All by Client ID
// /api/campaigns/{clientId}
// GET Campaign by ID
// /api/campaigns/id/{campaignId}
// POST Create Campaign
// /api/campaigns/create
// POST Duplicate Campaign
// /api/campaigns/duplicate
// POST Upload Form Config
// /api/campaigns/config/upload
// PUT
// /api/campaigns/edit
// DELETE
// /api/campaigns/delete/{campaignId}/{userId}
export const apiPathCampaigns = "/campaigns/";

//Messages
//GET .....
export const apiPathMessages = "/message/";
export const apiPathMessageFunctions = "/";
export const apiPathStopCompliance = "/";

// NAS Admin User Queries
// GET
export const apiPathNASUserEmail = "/User/";

// Email Subscription Status
// GET
export const apiPathEmailSubscriptionStatus = apiPathMessages + "subscriptionstatus/";
// PUT
export const apiPathManageSubscription = apiPathMessages + "managesubscription";

/**
 * Reporting endpoints
 */
export const apiPathReporting = "/reporting/";
// GET
export const apiPathCampaignEmailPerformanceReport = apiPathReporting + "campaignemailperformance/";
export const apiPathCampaignTextPerformanceReport = apiPathReporting + "campaigntextperformance/";

/**
 * ABAC endpoints
 */
// GET
export const abacPermissionControllerPath = "/Permission/";
